import { useId, type ReactNode } from "react";
import type { PlayerAIToolProposalPayload } from "./playerai.types";
import {
  proposalDisplayStatus,
  PROPOSAL_STATUS_CLASS,
  PROPOSAL_STATUS_LABEL,
} from "./proposal-status";
import { proposalTitle } from "./proposal-presentation";

/** Shared presentation only: hosts retain every approval and selection callback. */
export function ProposalCardFrame({
  payload,
  children,
  testId,
  canvasDeliveryState,
}: {
  payload: PlayerAIToolProposalPayload;
  children: ReactNode;
  testId?: string;
  canvasDeliveryState?: "applied" | "pending" | "failed";
}) {
  const titleId = useId();
  const statusId = useId();
  // A fresh delivery result supersedes the proposal's saved execution snapshot.
  const status =
    payload.status === "applied" && canvasDeliveryState === "applied"
      ? {
          label: PROPOSAL_STATUS_LABEL.applied,
          className: PROPOSAL_STATUS_CLASS.applied,
        }
      : proposalDisplayStatus(payload);
  const isAwaitingCanvas =
    payload.status === "applied" && canvasDeliveryState === "pending";
  const canvasFailed =
    payload.status === "applied" && canvasDeliveryState === "failed";
  const statusLabel = isAwaitingCanvas
    ? "Saved; waiting for canvas"
    : canvasFailed
      ? "Canvas not delivered"
      : status.label;
  const statusClass = isAwaitingCanvas
    ? "border-pb-amber/40 bg-pb-amber-soft text-pb-amber-text"
    : canvasFailed
      ? "border-pb-red/40 bg-pb-red-soft text-pb-red-text"
      : status.className;
  // PB-5447: the card is where Review lands — a group named by its own title
  // and status, focusable by script only, wearing the reveal highlight on its
  // own edges rather than the row's.
  return (
    <div
      data-testid={testId}
      data-proposal-card
      role="group"
      aria-labelledby={`${titleId} ${statusId}`}
      tabIndex={-1}
      className="mt-2 min-w-0 w-full rounded-md border border-pb-border bg-pb-card/90 p-2.5 [overflow-wrap:anywhere] focus-ring"
    >
      <div className="flex items-start gap-2">
        <div
          id={titleId}
          className="min-w-0 flex-1 text-xs font-semibold text-pb-text"
        >
          {proposalTitle(payload)}
        </div>
        <span
          id={statusId}
          className={`shrink-0 rounded-full border px-1.5 py-0.5 text-dense-2 font-medium ${statusClass}`}
        >
          {statusLabel}
        </span>
      </div>
      {children}
    </div>
  );
}
